import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { Button, Icon, Popover2, PageHeader, Modal, Loading } from '@evertrue/et-components';
import { FlexTable, RichTextEditor } from '@evertrue/sdk';
import { useFluxStore } from '@evertrue/et-flux';
import EverTrue from 'app';
import { useEmailTemplatesProvider } from 'apps/outreach/email/templates/email-templates-context';
import { useEmailSignaturesProvider } from 'apps/outreach/email/signatures/email-signatures-context';
import cadenceGqlCall from 'apps/cadences-v2/utils/cadence-gql-call';
import { REGISTER_TAG_MUTATION, ORG_TAGS_QUERY } from 'apps/cadences-v2/utils/cadence-queries';
import SignaturePickerModal from 'apps/cadences-v2/components/email-template/signature-picker-modal';
import AllUsersSource from 'apps/users/sources/all-users-source';
import AllUsersStore from 'apps/users/stores/all-users-store';
import { matchesDateFilter } from 'apps/cadences-v2/components/date-filter-popover';
import FilterBar from 'apps/cadences-v2/components/filter-bar';
import { CreatedByCell } from 'apps/cadences-v2/components/list-cell-renderers';
import useFileUploader from 'components/emailer/hooks/use-file-uploader';

/**
 * Strips all HTML tags from a string by iterating until no tags remain.
 */
const stripHtmlTags = (html) => {
  let text = html;
  let prev;
  do {
    prev = text;
    text = text.replace(/<[^>]*>/g, '');
  } while (text !== prev);
  return text;
};

/** Normalize tags that may arrive as a comma-separated string from the API. */
const parseTags = (tags) => {
  if (Array.isArray(tags)) return tags.filter(Boolean);
  if (typeof tags === 'string' && tags.trim()) {
    return tags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
  }
  return [];
};

/**
 * Dynamic field placeholders for rich text editor.
 * Matches the merge fields supported by outreach CompileResource.
 */
const DYNAMIC_FIELD_PLACEHOLDERS = {
  Prefix: '{{prefix}}',
  'First Name': '{{name_first}}',
  'Last Name': '{{name_last}}',
  Nickname: '{{name_nick}}',
  'Primary Phone': '{{primary_phone}}',
  'Primary Email': '{{primary_email}}',
  'Your Name': '{{user_name}}',
};

/** Tab definitions. */
const TABS = [
  { id: 'rich-text', label: 'Email Templates' },
  { id: 'signatures', label: 'Your Email Signatures' },
];

/**
 * EmailTemplatesController is the main page for managing email templates and signatures.
 * Uses real outreach API endpoints via EmailTemplatesProvider and EmailSignaturesProvider.
 */
const EmailTemplatesController = () => {
  const {
    templates: rawTemplates,
    loading: templatesLoading,
    error: templatesError,
    createEmailTemplate,
    updateEmailTemplate,
    deleteEmailTemplate,
    fetchTemplates,
    handleSearch: searchTemplates,
  } = useEmailTemplatesProvider();

  const {
    signatures: rawSignatures,
    loading: signaturesLoading,
    error: signaturesError,
    addEmailSignature,
    updateEmailSignature,
    deleteEmailSignature,
    refreshSignatures,
  } = useEmailSignaturesProvider();

  const allUsers = useFluxStore(AllUsersStore, () => AllUsersStore.getAllUsers());
  const currentUserId = EverTrue.store?.user?.get('id');

  useEffect(() => {
    const ids = [
      ...rawTemplates.map((t) => t.userId || t.user_id),
      ...rawSignatures.filter((s) => s.id !== 0).map((s) => s.userId || s.user_id),
    ].filter(Boolean);
    if (ids.length > 0) AllUsersSource.fetchUsers(ids);
  }, [rawTemplates, rawSignatures]);

  // Live tag suggestions from cadence org tag registry (same source as cadence create form)
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [sigTagSuggestions, setSigTagSuggestions] = useState([]);

  const [selectedTab, setSelectedTab] = useState('rich-text');
  const [searchQuery, setSearchQuery] = useState('');

  // Filter state — driven by FilterBar
  const [selectedTags, setSelectedTags] = useState([]);
  const [createdByFilter, setCreatedByFilter] = useState([]);
  const [createdDateFilter, setCreatedDateFilter] = useState({ operator: 'all', date1: null, date2: null });
  const [defaultFilter, setDefaultFilter] = useState([]);
  // Optional filter state
  const [titleFilter, setTitleFilter] = useState('');
  const [updatedDateFilter, setUpdatedDateFilter] = useState({ operator: 'all', date1: null, date2: null });

  // Inline rename state
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  // Signature modal state
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [editingSignature, setEditingSignature] = useState(null);
  const [signatureName, setSignatureName] = useState('');
  const [signatureContent, setSignatureContent] = useState('');
  const [signatureTags, setSignatureTags] = useState([]);
  const [signatureTagInput, setSignatureTagInput] = useState('');

  // Template editor modal state
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [templateSubject, setTemplateSubject] = useState('');
  const [templateBody, setTemplateBody] = useState('');
  const [templateTags, setTemplateTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [showSignaturePicker, setShowSignaturePicker] = useState(false);

  useEffect(() => {
    if (!tagInput.trim()) {
      setTagSuggestions([]);
      return;
    }
    let stale = false;
    const timer = setTimeout(() => {
      cadenceGqlCall(ORG_TAGS_QUERY, { search: tagInput.trim() })
        .then((data) => {
          if (!stale) setTagSuggestions(data?.orgTags || []);
        })
        .catch(() => {
          if (!stale) setTagSuggestions([]);
        });
    }, 300);
    return () => {
      stale = true;
      clearTimeout(timer);
    };
  }, [tagInput]);

  useEffect(() => {
    if (!signatureTagInput.trim()) {
      setSigTagSuggestions([]);
      return;
    }
    let stale = false;
    const timer = setTimeout(() => {
      cadenceGqlCall(ORG_TAGS_QUERY, { search: signatureTagInput.trim() })
        .then((data) => {
          if (!stale) setSigTagSuggestions(data?.orgTags || []);
        })
        .catch(() => {
          if (!stale) setSigTagSuggestions([]);
        });
    }, 300);
    return () => {
      stale = true;
      clearTimeout(timer);
    };
  }, [signatureTagInput]);

  const handleAddTag = (val) => {
    const tag = (val !== undefined ? val : tagInput).trim();
    if (!tag || templateTags.includes(tag)) {
      setTagInput('');
      return;
    }
    setTemplateTags((prev) => [...prev, tag]);
    setTagInput('');
    setTagSuggestions([]);
    cadenceGqlCall(REGISTER_TAG_MUTATION, { name: tag }).catch(() => {});
  };

  const handleAddSigTag = (val) => {
    const tag = (val !== undefined ? val : signatureTagInput).trim();
    if (!tag || signatureTags.includes(tag)) {
      setSignatureTagInput('');
      return;
    }
    setSignatureTags((prev) => [...prev, tag]);
    setSignatureTagInput('');
    setSigTagSuggestions([]);
    cadenceGqlCall(REGISTER_TAG_MUTATION, { name: tag }).catch(() => {});
  };
  const [appliedSignatureId, setAppliedSignatureId] = useState(null);
  const templateEditorRef = useRef(null);
  const signatureEditorRef = useRef(null);
  const { onImageUpload } = useFileUploader([], null);

  /** Map outreach API template shape to display row. */
  const templates = rawTemplates.map((t) => {
    const userId = t.userId || t.user_id;
    const user = allUsers[userId];
    return {
      id: t.id,
      name: t.templateName || t.template_name || '',
      subject: t.templateSubject || t.template_subject || '',
      body: t.templateBody || t.template_body || '',
      dynamicTemplate: t.dynamicTemplate || t.dynamic_template || false,
      createdDate: t.createdAt || t.created_at || '',
      lastUpdated: t.updatedAt || t.updated_at || '',
      tags: parseTags(t.tags),
      oid: t.oid,
      userId,
      createdBy: user
        ? {
            id: userId,
            name: user.name,
            initials: user.name
              ? user.name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 2)
              : '',
          }
        : userId
          ? { id: userId, name: null }
          : null,
      _raw: t,
    };
  });

  /** Map outreach API signature shape to display row (filter out "No Signature" placeholder). */
  const signatures = rawSignatures
    .filter((s) => s.id !== 0)
    .map((s) => {
      const userId = s.userId || s.user_id;
      const user = allUsers[userId];
      return {
        id: s.id,
        name: s.signature_name || s.signatureName || '',
        preview: s.signature_body || s.signatureBody || '',
        isDefault: !!s.default,
        createdDate: s.createdAt || s.created_at || '',
        lastUpdated: s.updatedAt || s.updated_at || '',
        tags: s.tags || [],
        userId,
        createdBy: user
          ? {
              id: userId,
              name: user.name,
              initials: user.name
                ? user.name
                    .split(' ')
                    .map((n) => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2)
                : '',
            }
          : userId
            ? { id: userId, name: null }
            : null,
        _raw: s,
      };
    });

  const allCreatorOptions = useMemo(() => {
    const creatorMap = {};
    [...templates, ...signatures].forEach((item) => {
      if (item.createdBy?.id && item.createdBy?.name) {
        creatorMap[item.createdBy.id] = item.createdBy.name;
      }
    });
    return Object.entries(creatorMap)
      .map(([id, name]) => ({ label: name, value: Number(id) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [templates, signatures]);

  const isLoading = selectedTab === 'signatures' ? signaturesLoading : templatesLoading;
  const fetchError = selectedTab === 'signatures' ? signaturesError : templatesError;

  const handleCreateNew = useCallback((type) => {
    if (type === 'rich-text') {
      setEditingTemplate(null);
      setTemplateName('');
      setTemplateSubject('');
      setTemplateBody('');
      setTemplateTags([]);
      setTagInput('');
      setAppliedSignatureId(null);
      setShowTemplateModal(true);
    } else if (type === 'signature') {
      setEditingSignature(null);
      setSignatureName('');
      setSignatureContent('');
      setSignatureTags([]);
      setSignatureTagInput('');
      setShowSignatureModal(true);
    }
  }, []);

  const handleEditTemplate = useCallback(
    (templateId, tab) => {
      if (tab === 'signatures') {
        const sig = signatures.find((s) => s.id === templateId);
        if (sig) {
          setEditingSignature(sig);
          setSignatureName(sig.name);
          setSignatureContent(sig.preview);
          setSignatureTags(sig.tags || []);
          setSignatureTagInput('');
          setShowSignatureModal(true);
        }
      } else {
        const template = templates.find((t) => t.id === templateId);
        if (template) {
          setEditingTemplate(template);
          setTemplateName(template.name);
          setTemplateSubject(template.subject);
          setTemplateBody(template.body);
          setTemplateTags(parseTags(template.tags));
          setTagInput('');
          setAppliedSignatureId(null);
          setShowTemplateModal(true);
        }
      }
    },
    [signatures, templates]
  );

  const handleDuplicateTemplate = useCallback(
    async (templateId) => {
      const template = templates.find((t) => t.id === templateId);
      if (!template) return;
      try {
        await createEmailTemplate({
          templateName: `${template.name} (Copy)`,
          templateSubject: template.subject,
          templateBody: template.body,
          dynamicTemplate: template.dynamicTemplate,
          tags: template.tags || [],
        });
      } catch {
        // Error handled by context
      }
    },
    [templates, createEmailTemplate]
  );

  const handleDeleteTemplate = useCallback(
    async (templateId, tab) => {
      try {
        if (tab === 'signatures') {
          await deleteEmailSignature(templateId);
        } else {
          await deleteEmailTemplate(templateId);
        }
      } catch {
        // Error handled by context
      }
    },
    [deleteEmailTemplate, deleteEmailSignature]
  );

  const handleDuplicateSignature = useCallback(
    async (signatureId) => {
      const sig = signatures.find((s) => s.id === signatureId);
      if (!sig) return;
      try {
        const newSig = await addEmailSignature(`${sig.name} (Copy)`, sig.preview || sig._raw?.signature_body || '');
        // Copy tags to the new signature (addEmailSignature doesn't support tags)
        const tags = sig._raw?.tags || sig.tags || [];
        if (newSig?.id && tags.length > 0) {
          await updateEmailSignature(newSig.id, { tags });
        }
        refreshSignatures();
      } catch {
        // Error handled by context
      }
    },
    [signatures, addEmailSignature, updateEmailSignature, refreshSignatures]
  );

  const handleSaveSignature = useCallback(async () => {
    if (!signatureName.trim()) {
      EverTrue.Alert.error('Please enter a signature name');
      return;
    }
    try {
      if (editingSignature) {
        await updateEmailSignature(editingSignature.id, {
          signature_name: signatureName,
          signature_body: signatureContent,
          tags: signatureTags,
        });
      } else {
        const newSig = await addEmailSignature(signatureName);
        if (newSig) {
          await updateEmailSignature(newSig.id, {
            signature_name: signatureName,
            signature_body: signatureContent || '',
            tags: signatureTags,
          });
        }
      }
      refreshSignatures();
      setShowSignatureModal(false);
    } catch {
      // Error handled by context
    }
  }, [
    signatureName,
    signatureContent,
    signatureTags,
    editingSignature,
    updateEmailSignature,
    addEmailSignature,
    refreshSignatures,
  ]);

  const handleSaveTemplate = useCallback(async () => {
    if (!templateName.trim()) {
      EverTrue.Alert.error('Please enter a template name');
      return;
    }
    try {
      if (editingTemplate) {
        await updateEmailTemplate(editingTemplate.id, editingTemplate.oid, editingTemplate.userId, {
          templateName,
          templateSubject,
          templateBody,
          dynamicTemplate: editingTemplate.dynamicTemplate,
          tags: templateTags,
        });
      } else {
        await createEmailTemplate({
          templateName,
          templateSubject,
          templateBody,
          dynamicTemplate: false,
          tags: templateTags,
        });
      }
      setShowTemplateModal(false);
    } catch {
      // Error handled by context
    }
  }, [
    templateName,
    templateSubject,
    templateBody,
    templateTags,
    editingTemplate,
    updateEmailTemplate,
    createEmailTemplate,
  ]);

  const handleTabChange = useCallback((tabId) => {
    setSelectedTab(tabId);
    setSearchQuery('');
    if (tabId !== 'signatures') setDefaultFilter([]);
  }, []);

  const handleSearchChange = useCallback(
    (value) => {
      setSearchQuery(value);
      if (selectedTab === 'rich-text') {
        searchTemplates(value);
      }
    },
    [selectedTab, searchTemplates]
  );

  const getCurrentData = () => (selectedTab === 'signatures' ? signatures : templates);

  const filterData = (data) => {
    return data.filter((item) => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const name = (item.name || '').toLowerCase();
        const subject = (item.subject || '').toLowerCase();
        if (!name.includes(query) && !subject.includes(query)) return false;
      }
      if (selectedTags.length > 0) {
        const itemTags = item.tags || [];
        if (!selectedTags.some((t) => itemTags.includes(t))) return false;
      }
      if (createdByFilter.length > 0) {
        const creatorId = item._raw?.userId || item._raw?.user_id;
        if (!createdByFilter.some((o) => o.value === creatorId)) return false;
      }
      if (!matchesDateFilter(item.createdDate, createdDateFilter)) return false;
      if (!matchesDateFilter(item.lastUpdated, updatedDateFilter)) return false;
      if (titleFilter) {
        const name = (item.name || '').toLowerCase();
        if (!name.includes(titleFilter.toLowerCase())) return false;
      }
      if (defaultFilter.length > 0) {
        const wantsYes = defaultFilter.some((f) => f.value === 'yes');
        const wantsNo = defaultFilter.some((f) => f.value === 'no');
        if (wantsYes && !wantsNo && !item.isDefault) return false;
        if (wantsNo && !wantsYes && item.isDefault) return false;
      }
      return true;
    });
  };

  /** Collect all unique tags across templates and signatures for the filter dropdown. */
  const allTags = [
    ...new Set([...templates.flatMap((item) => item.tags || []), ...signatures.flatMap((item) => item.tags || [])]),
  ].sort();

  const currentData = getCurrentData();
  const filteredData = filterData(currentData);
  const total = filteredData.length;

  /** Formats a date string or unix timestamp for display. Returns "—" for falsy/invalid values. */
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      // API may return unix seconds (e.g. 1772831315.000000000) — convert to ms
      let date;
      const num = typeof dateStr === 'string' ? parseFloat(dateStr) : dateStr;
      if (typeof num === 'number' && !isNaN(num) && num > 1e9 && num < 1e11) {
        date = new Date(num * 1000);
      } else if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        // Date-only strings: parse as local midnight to avoid UTC offset shifting the day
        const [y, m, d] = dateStr.split('-').map(Number);
        date = new Date(y, m - 1, d);
      } else {
        date = new Date(dateStr);
      }
      if (isNaN(date.getTime()) || date.getFullYear() < 2000) return '—';
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return '—';
    }
  };

  /** Parse a date value (ISO string or unix seconds) to epoch ms for sorting. */
  const parseTimestampMs = (value) => {
    if (!value) return 0;
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (typeof num === 'number' && !isNaN(num) && num > 1e9 && num < 1e11) return num * 1000;
    const d = new Date(value);
    return isNaN(d.getTime()) ? 0 : d.getTime();
  };

  const handleStartRename = useCallback((id, currentName) => {
    setRenamingId(id);
    setRenameValue(currentName);
  }, []);

  const handleSubmitRename = useCallback(
    async (id) => {
      // Clear immediately to prevent double-submit from blur after Enter
      setRenamingId(null);

      const newName = renameValue.trim();
      if (!newName) return;

      // Try templates first, then signatures
      const template = templates.find((t) => t.id === id);
      if (template) {
        if (newName === template.name) return;
        try {
          await updateEmailTemplate(id, template.oid, template.userId, {
            templateName: newName,
            templateSubject: template.subject,
            templateBody: template.body,
            dynamicTemplate: template.dynamicTemplate,
            tags: template.tags || [],
          });
        } catch {
          // Error handled by context
        }
        return;
      }

      const sig = signatures.find((s) => s.id === id);
      if (sig) {
        if (newName === sig.name) return;
        try {
          await updateEmailSignature(id, {
            ...sig._raw,
            signature_name: newName,
          });
          refreshSignatures();
        } catch {
          // Error handled by context
        }
      }
    },
    [renameValue, templates, signatures, updateEmailTemplate, updateEmailSignature, refreshSignatures]
  );

  const renderTemplateActionMenu = useCallback(
    (template) => (
      <Popover2
        trigger={
          <Button type="simple" className="email-templates--action-btn">
            <Icon icon="more-horiz" />
          </Button>
        }
      >
        {({ closePopover }) => (
          <div className="email-templates--action-menu">
            <button
              type="button"
              onClick={() => {
                closePopover();
                handleEditTemplate(template.id, 'rich-text');
              }}
            >
              <Icon icon="edit" />
              <span>Edit</span>
            </button>
            <button
              type="button"
              onClick={() => {
                closePopover();
                handleStartRename(template.id, template.name);
              }}
            >
              <Icon icon="text-format" iconKit="gt2" />
              <span>Rename</span>
            </button>
            <button
              type="button"
              onClick={() => {
                closePopover();
                handleDuplicateTemplate(template.id);
              }}
            >
              <Icon icon="content-copy" iconKit="gt2" />
              <span>Duplicate</span>
            </button>
            <button type="button" className="email-templates--action-disabled" disabled>
              <Icon icon="share" />
              <span>Share</span>
              <span className="email-templates--coming-soon-badge">Coming Soon</span>
            </button>
            <button
              type="button"
              className="email-templates--action-danger"
              onClick={() => {
                closePopover();
                handleDeleteTemplate(template.id, 'rich-text');
              }}
            >
              <Icon icon="delete" />
              <span>Delete</span>
            </button>
          </div>
        )}
      </Popover2>
    ),
    [handleEditTemplate, handleStartRename, handleDuplicateTemplate, handleDeleteTemplate]
  );

  const renderSignatureActionMenu = useCallback(
    (signature) => (
      <Popover2
        trigger={
          <Button type="simple" className="email-templates--action-btn">
            <Icon icon="more-horiz" />
          </Button>
        }
      >
        {({ closePopover }) => (
          <div className="email-templates--action-menu">
            <button
              type="button"
              onClick={() => {
                closePopover();
                handleEditTemplate(signature.id, 'signatures');
              }}
            >
              <Icon icon="edit" />
              <span>Edit</span>
            </button>
            <button
              type="button"
              onClick={() => {
                closePopover();
                handleStartRename(signature.id, signature.name);
              }}
            >
              <Icon icon="text-format" iconKit="gt2" />
              <span>Rename</span>
            </button>
            <button
              type="button"
              onClick={() => {
                closePopover();
                handleDuplicateSignature(signature.id);
              }}
            >
              <Icon icon="content-copy" iconKit="gt2" />
              <span>Duplicate</span>
            </button>
            <button
              type="button"
              disabled={signature.isDefault}
              className={signature.isDefault ? 'email-templates--action-disabled' : ''}
              onClick={async () => {
                closePopover();
                try {
                  await updateEmailSignature(signature.id, { default: true });
                  refreshSignatures();
                  EverTrue.Alert.success('Signature set as default');
                } catch {
                  // Error handled by context
                }
              }}
            >
              <Icon icon="star" iconKit="gt2" />
              <span>{signature.isDefault ? 'Default' : 'Set as Default'}</span>
            </button>
            <button type="button" className="email-templates--action-disabled" disabled>
              <Icon icon="share" />
              <span>Share</span>
              <span className="email-templates--coming-soon-badge">Coming Soon</span>
            </button>
            <button
              type="button"
              className="email-templates--action-danger"
              onClick={() => {
                closePopover();
                handleDeleteTemplate(signature.id, 'signatures');
              }}
            >
              <Icon icon="delete" />
              <span>Delete</span>
            </button>
          </div>
        )}
      </Popover2>
    ),
    [
      handleEditTemplate,
      handleStartRename,
      handleDuplicateSignature,
      handleDeleteTemplate,
      updateEmailSignature,
      refreshSignatures,
    ]
  );

  const templateColumns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Title',
        flex: 1,
        minWidth: 180,
        sortable: true,
        hideable: false,
        renderCell: ({ row }) =>
          String(row.id) === String(renamingId) ? (
            <div className="email-templates--name-cell">
              <input
                type="text"
                className="cadences-v2-table--rename-input"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onBlur={() => handleSubmitRename(row.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSubmitRename(row.id);
                  else if (e.key === 'Escape') setRenamingId(null);
                  else e.stopPropagation();
                }}
                autoFocus
              />
            </div>
          ) : (
            <div className="email-templates--name-cell">
              <button
                type="button"
                className="email-templates--name-link"
                onClick={() => handleEditTemplate(row.id, 'rich-text')}
              >
                {row.name}
              </button>
            </div>
          ),
      },
      {
        field: 'tags',
        headerName: 'Tags',
        flex: 0.7,
        minWidth: 120,
        sortable: false,
        hideable: false,
        renderCell: ({ row }) =>
          (row.tags || []).length > 0 ? (
            <div className="email-templates--tags-cell">
              {row.tags.map((tag) => (
                <span key={tag} className="email-templates--tag-badge">
                  {tag}
                </span>
              ))}
            </div>
          ) : (
            '—'
          ),
      },
      {
        field: 'createdDate',
        headerName: 'Created Date',
        flex: 0.7,
        minWidth: 120,
        sortable: true,
        hideable: false,
        valueGetter: (value) => parseTimestampMs(value),
        renderCell: ({ row }) => formatDate(row.createdDate),
      },
      {
        field: 'lastUpdated',
        headerName: 'Updated Date',
        flex: 0.7,
        minWidth: 120,
        sortable: true,
        valueGetter: (value) => parseTimestampMs(value),
        renderCell: ({ row }) => formatDate(row.lastUpdated),
      },
      {
        field: 'createdBy',
        headerName: 'Created By',
        flex: 0.7,
        minWidth: 120,
        sortable: true,
        valueGetter: (value) => value?.name || '',
        renderCell: ({ row }) => <CreatedByCell createdBy={row.createdBy} currentUserId={currentUserId} />,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 80,
        sortable: false,
        filterable: false,
        hideable: false,
        type: 'actions',
        renderCell: ({ row }) => renderTemplateActionMenu(row),
      },
    ],
    [handleEditTemplate, renderTemplateActionMenu, currentUserId, renamingId, renameValue, handleSubmitRename]
  );

  const signatureColumns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Title',
        flex: 1,
        minWidth: 180,
        sortable: true,
        hideable: false,
        renderCell: ({ row }) =>
          String(row.id) === String(renamingId) ? (
            <div className="email-templates--name-cell">
              <input
                type="text"
                className="cadences-v2-table--rename-input"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onBlur={() => handleSubmitRename(row.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSubmitRename(row.id);
                  else if (e.key === 'Escape') setRenamingId(null);
                  else e.stopPropagation();
                }}
                autoFocus
              />
            </div>
          ) : (
            <div className="email-templates--name-cell">
              <button
                type="button"
                className="email-templates--name-link"
                onClick={() => handleEditTemplate(row.id, 'signatures')}
              >
                {row.name}
              </button>
            </div>
          ),
      },
      {
        field: 'tags',
        headerName: 'Tags',
        flex: 0.7,
        minWidth: 120,
        sortable: false,
        hideable: false,
        renderCell: ({ row }) =>
          (row.tags || []).length > 0 ? (
            <div className="email-templates--tags-cell">
              {row.tags.map((tag) => (
                <span key={tag} className="email-templates--tag-badge">
                  {tag}
                </span>
              ))}
            </div>
          ) : (
            '—'
          ),
      },
      {
        field: 'createdDate',
        headerName: 'Created Date',
        flex: 0.7,
        minWidth: 120,
        sortable: true,
        hideable: false,
        valueGetter: (value) => parseTimestampMs(value),
        renderCell: ({ row }) => formatDate(row.createdDate),
      },
      {
        field: 'lastUpdated',
        headerName: 'Updated Date',
        flex: 0.7,
        minWidth: 120,
        sortable: true,
        valueGetter: (value) => parseTimestampMs(value),
        renderCell: ({ row }) => formatDate(row.lastUpdated),
      },
      {
        field: 'createdBy',
        headerName: 'Created By',
        flex: 0.7,
        minWidth: 120,
        sortable: true,
        valueGetter: (value) => value?.name || '',
        renderCell: ({ row }) => <CreatedByCell createdBy={row.createdBy} currentUserId={currentUserId} />,
      },
      {
        field: 'isDefault',
        headerName: 'Default?',
        width: 90,
        sortable: true,
        renderCell: ({ row }) => (row.isDefault ? 'Yes' : '—'),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 80,
        sortable: false,
        filterable: false,
        hideable: false,
        type: 'actions',
        renderCell: ({ row }) => renderSignatureActionMenu(row),
      },
    ],
    [handleEditTemplate, renderSignatureActionMenu, currentUserId, renamingId, renameValue, handleSubmitRename]
  );

  const renderCreateDropdown = () => (
    <Popover2
      trigger={
        <Button type="action">
          <Icon icon="add" /> Create New
        </Button>
      }
    >
      {({ closePopover }) => (
        <div className="email-templates--action-menu">
          <button
            type="button"
            onClick={() => {
              closePopover();
              handleCreateNew('rich-text');
            }}
          >
            <Icon icon="email" />
            <span>Email Template</span>
          </button>
          <button
            type="button"
            onClick={() => {
              closePopover();
              handleCreateNew('signature');
            }}
          >
            <Icon icon="edit" />
            <span>Email Signature</span>
          </button>
        </div>
      )}
    </Popover2>
  );

  const getEmptyState = () => {
    if (selectedTab === 'signatures') {
      return {
        icon: 'edit',
        title: 'No Signatures Yet',
        description: 'Create your first email signature to use in your emails',
        buttonText: '+ New Signature',
        action: () => handleCreateNew('signature'),
      };
    }
    return {
      icon: 'email',
      title: 'No Templates Found',
      description: 'Create your first email template to get started',
      buttonText: '+ New Template',
      action: () => handleCreateNew('rich-text'),
    };
  };

  const emptyState = getEmptyState();

  if (isLoading && currentData.length === 0) {
    return <Loading />;
  }

  return (
    <div className="email-templates-page">
      <div className="email-templates-page--header">
        <PageHeader title="Email Templates" subtitle="Manage your email templates for constituent outreach" />
        <div className="email-templates-page--header-actions">{renderCreateDropdown()}</div>
      </div>

      <div className="email-templates-page--tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`email-templates-page--tab ${selectedTab === tab.id ? 'is-active' : ''}`}
            onClick={() => handleTabChange(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {fetchError && (
        <div className="email-templates-page--error">
          <Icon icon="error-outline" />
          <span>{fetchError}</span>
          <Button type="simple" onClick={selectedTab === 'signatures' ? refreshSignatures : () => fetchTemplates(0)}>
            Retry
          </Button>
        </div>
      )}

      <FilterBar
        rightContent={
          <div className="email-templates-page--toolbar-right">
            <span className="email-templates-page--count">
              {total} {selectedTab === 'signatures' ? 'Signatures' : 'Templates'}
            </span>
            <div className="email-templates-page--search">
              <Icon icon="search" />
              <input
                type="text"
                placeholder={`Search ${selectedTab === 'signatures' ? 'signatures' : 'templates'}...`}
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
            </div>
          </div>
        }
        filters={[
          {
            id: 'createdBy',
            label: 'Created By',
            type: 'multiselect',
            options: allCreatorOptions,
            value: createdByFilter,
            onChange: setCreatedByFilter,
            alwaysVisible: true,
            alwaysShowLabel: true,
            searchable: true,
          },
          {
            id: 'tags',
            label: 'Tags',
            type: 'multiselect',
            options: allTags.map((t) => ({ label: t, value: t })),
            value: selectedTags.map((t) => ({ label: t, value: t })),
            onChange: (vals) => setSelectedTags(vals.map((v) => v.value)),
            alwaysVisible: true,
            alwaysShowLabel: true,
            searchable: true,
          },
          {
            id: 'creationDate',
            label: 'Creation Date',
            type: 'date',
            value: createdDateFilter,
            onChange: setCreatedDateFilter,
            alwaysVisible: true,
            alwaysShowLabel: true,
          },
          ...(selectedTab === 'signatures'
            ? [
                {
                  id: 'isDefault',
                  label: 'Default?',
                  type: 'multiselect',
                  options: [
                    { label: 'Yes', value: 'yes' },
                    { label: 'No', value: 'no' },
                  ],
                  value: defaultFilter,
                  onChange: setDefaultFilter,
                  alwaysVisible: true,
                  alwaysShowLabel: true,
                },
              ]
            : []),
          {
            id: 'title',
            label: 'Title',
            type: 'text',
            value: titleFilter,
            onChange: setTitleFilter,
            alwaysVisible: false,
          },
          {
            id: 'updatedDate',
            label: 'Updated Date',
            type: 'date',
            value: updatedDateFilter,
            onChange: setUpdatedDateFilter,
            alwaysVisible: false,
          },
        ]}
      />

      <div className="email-templates-page--table-container" style={{ height: 'calc(100vh - 270px)' }}>
        {selectedTab === 'rich-text' ? (
          <FlexTable
            tableKey="email-templates-list"
            columns={templateColumns}
            rows={filteredData}
            loading={isLoading}
            showToolbar
            disableColumnMenu={true}
            showColumnVerticalBorder={false}
            sortingMode="client"
            sortingOrder={['asc', 'desc']}
            paginationMode="client"
            pageSizeOptions={[10, 25, 50, 100]}
            slotProps={{
              columnsManagement: {
                getTogglableColumns: () => ['lastUpdated', 'createdBy'],
              },
              toolbar: { showExport: false, showQuickFilter: false },
            }}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 25 } },
              sorting: { sortModel: [{ field: 'createdDate', sort: 'desc' }] },
              columns: {
                columnVisibilityModel: {
                  lastUpdated: false,
                  createdBy: false,
                },
              },
            }}
            getRowHeight={() => 'auto'}
            emptyStateMessage={emptyState.title}
            emptyStateIcon={<Icon icon={emptyState.icon} size={5} />}
            disableRowSelectionOnClick
          />
        ) : (
          <FlexTable
            tableKey="email-signatures-list"
            columns={signatureColumns}
            rows={filteredData}
            loading={isLoading}
            showToolbar
            disableColumnMenu={true}
            showColumnVerticalBorder={false}
            sortingMode="client"
            sortingOrder={['asc', 'desc']}
            paginationMode="client"
            pageSizeOptions={[10, 25, 50, 100]}
            slotProps={{
              columnsManagement: {
                getTogglableColumns: () => ['lastUpdated', 'createdBy', 'isDefault'],
              },
              toolbar: { showExport: false, showQuickFilter: false },
            }}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 25 } },
              sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
              columns: {
                columnVisibilityModel: {
                  lastUpdated: false,
                  createdBy: false,
                  isDefault: false,
                },
              },
            }}
            getRowHeight={() => 'auto'}
            emptyStateMessage={emptyState.title}
            emptyStateIcon={<Icon icon={emptyState.icon} size={5} />}
            disableRowSelectionOnClick
          />
        )}
      </div>

      {/* Signature Create/Edit Modal */}
      <Modal isOpen={showSignatureModal} closeModal={() => setShowSignatureModal(false)} size="large">
        <div className="email-templates--signature-modal">
          <div className="email-templates--signature-modal-header">
            <h2>{editingSignature ? 'Edit Signature' : 'Create New Signature'}</h2>
            <div className="email-templates--signature-modal-header-actions">
              <button
                type="button"
                className="email-templates--signature-modal-cancel"
                onClick={() => setShowSignatureModal(false)}
              >
                Cancel
              </button>
              <Button type="action" onClick={handleSaveSignature}>
                Save
              </Button>
            </div>
          </div>
          <div className="email-templates--signature-modal-body">
            <div className="email-templates--template-modal-field">
              <label className="email-templates--template-modal-label">Signature Name</label>
              <input
                type="text"
                className="email-templates--template-modal-input"
                placeholder="Enter signature name..."
                value={signatureName}
                onChange={(e) => setSignatureName(e.target.value)}
              />
            </div>
            <div className="email-templates--template-modal-field">
              <label className="email-templates--template-modal-label">Signature Content</label>
              <div className="email-templates--template-modal-editor">
                <RichTextEditor
                  ref={signatureEditorRef}
                  value={signatureContent}
                  onChange={(newContent) => setSignatureContent(newContent)}
                  onImageUpload={onImageUpload}
                  config={{
                    height: '250px',
                    placeholder: 'Start typing your signature...',
                    resize: false,
                  }}
                />
              </div>
            </div>
            <div className="email-templates--template-modal-field">
              <label className="email-templates--template-modal-label">Tags</label>
              <div className="email-templates--template-modal-tags">
                <div className="email-templates--template-modal-tags-input-row">
                  <div className="email-templates--tag-input-wrapper">
                    <input
                      type="text"
                      className="email-templates--template-modal-input"
                      placeholder="Add a tag..."
                      value={signatureTagInput}
                      onChange={(e) => setSignatureTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddSigTag();
                        }
                      }}
                    />
                    {sigTagSuggestions.length > 0 && (
                      <div className="email-templates--tag-dropdown">
                        {sigTagSuggestions
                          .filter((t) => !signatureTags.includes(t))
                          .map((t) => (
                            <div
                              key={t}
                              className="email-templates--tag-dropdown-item"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handleAddSigTag(t);
                              }}
                            >
                              {t}
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                  <Button type="action" onClick={() => handleAddSigTag()} disabled={!signatureTagInput.trim()}>
                    Add
                  </Button>
                </div>
                {signatureTags.length > 0 && (
                  <div className="email-templates--template-modal-tags-list">
                    {signatureTags.map((tag) => (
                      <span key={tag} className="email-templates--template-modal-tag">
                        {tag}
                        <button type="button" onClick={() => setSignatureTags(signatureTags.filter((t) => t !== tag))}>
                          <Icon icon="close" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="email-templates--template-modal-footer">
            <div className="email-templates--template-modal-stats">
              <span>CHARACTERS: {stripHtmlTags(signatureContent).length}</span>
              <span>
                WORDS:{' '}
                {stripHtmlTags(signatureContent).trim()
                  ? stripHtmlTags(signatureContent).trim().split(/\s+/).length
                  : 0}
              </span>
            </div>
          </div>
        </div>
      </Modal>

      {/* Rich Text Template Create/Edit Modal */}
      <Modal isOpen={showTemplateModal} closeModal={() => setShowTemplateModal(false)} size="large">
        <div className="email-templates--template-modal">
          <div className="email-templates--template-modal-header">
            <h2>{editingTemplate ? 'Edit Email Template' : 'Create Email Template'}</h2>
            <div className="email-templates--template-modal-header-actions">
              <button
                type="button"
                className="email-templates--template-modal-cancel"
                onClick={() => setShowTemplateModal(false)}
              >
                Cancel
              </button>
              <Button type="action" onClick={handleSaveTemplate}>
                Save
              </Button>
            </div>
          </div>
          <div className="email-templates--template-modal-body">
            <div className="email-templates--template-modal-field">
              <label className="email-templates--template-modal-label">Template Name</label>
              <input
                type="text"
                className="email-templates--template-modal-input"
                placeholder="Enter template name..."
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
              />
            </div>
            <div className="email-templates--template-modal-field">
              <label className="email-templates--template-modal-label">Subject Line</label>
              <input
                type="text"
                className="email-templates--template-modal-input"
                placeholder="Enter email subject..."
                value={templateSubject}
                onChange={(e) => setTemplateSubject(e.target.value)}
              />
            </div>
            <div className="email-templates--template-modal-field">
              <label className="email-templates--template-modal-label">Tags</label>
              <div className="email-templates--template-modal-tags">
                <div className="email-templates--template-modal-tags-input-row">
                  <div className="email-templates--tag-input-wrapper">
                    <input
                      type="text"
                      className="email-templates--template-modal-input"
                      placeholder="Add a tag..."
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddTag();
                        }
                      }}
                    />
                    {tagSuggestions.length > 0 && (
                      <div className="email-templates--tag-dropdown">
                        {tagSuggestions
                          .filter((t) => !templateTags.includes(t))
                          .map((t) => (
                            <div
                              key={t}
                              className="email-templates--tag-dropdown-item"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handleAddTag(t);
                              }}
                            >
                              {t}
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                  <Button type="action" onClick={() => handleAddTag()} disabled={!tagInput.trim()}>
                    Add
                  </Button>
                </div>
                {templateTags.length > 0 && (
                  <div className="email-templates--template-modal-tags-list">
                    {templateTags.map((tag) => (
                      <span key={tag} className="email-templates--template-modal-tag">
                        {tag}
                        <button type="button" onClick={() => setTemplateTags(templateTags.filter((t) => t !== tag))}>
                          <Icon icon="close" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="email-templates--template-modal-field">
              <label className="email-templates--template-modal-label">Email Body</label>
              <div className="email-templates--template-modal-editor">
                <RichTextEditor
                  ref={templateEditorRef}
                  value={templateBody}
                  onChange={(newBody) => setTemplateBody(newBody)}
                  dynamicFields={DYNAMIC_FIELD_PLACEHOLDERS}
                  onImageUpload={onImageUpload}
                  config={{
                    height: '280px',
                    placeholder: 'Start typing...',
                    resize: false,
                  }}
                />
              </div>
            </div>
            <div className="email-form-signature-row">
              <Button
                type="secondary"
                onClick={() => setShowSignaturePicker(true)}
                className="email-form-signature-btn"
              >
                <Icon icon="edit" />
                {appliedSignatureId != null && appliedSignatureId !== 0
                  ? `Signature: ${rawSignatures.find((s) => s.id === appliedSignatureId)?.signature_name || 'Selected'}`
                  : 'Add Signature'}
              </Button>
              {appliedSignatureId != null && appliedSignatureId !== 0 && (
                <Button
                  type="simple"
                  onClick={() => {
                    setAppliedSignatureId(null);
                    const sigSep = '<!-- et-signature -->';
                    const sepIdx = templateBody.indexOf(sigSep);
                    if (sepIdx !== -1) {
                      setTemplateBody(templateBody.slice(0, sepIdx));
                    }
                  }}
                >
                  <Icon icon="close" /> Remove Signature
                </Button>
              )}
            </div>
          </div>
          <div className="email-templates--template-modal-footer">
            <div className="email-templates--template-modal-stats">
              <span>CHARACTERS: {stripHtmlTags(templateBody).length}</span>
              <span>
                WORDS: {stripHtmlTags(templateBody).trim() ? stripHtmlTags(templateBody).trim().split(/\s+/).length : 0}
              </span>
            </div>
          </div>
        </div>
      </Modal>

      <SignaturePickerModal
        isOpen={showSignaturePicker}
        onClose={() => setShowSignaturePicker(false)}
        signatures={rawSignatures || []}
        onSelect={(sig) => {
          if (sig.id === 0) {
            setAppliedSignatureId(null);
            const sigSep = '<!-- et-signature -->';
            const sepIdx = templateBody.indexOf(sigSep);
            if (sepIdx !== -1) {
              setTemplateBody(templateBody.slice(0, sepIdx));
            }
          } else {
            setAppliedSignatureId(sig.id);
            const sigSep = '<!-- et-signature -->';
            const baseBody = templateBody.includes(sigSep)
              ? templateBody.slice(0, templateBody.indexOf(sigSep))
              : templateBody;
            setTemplateBody(
              `${baseBody}${sigSep}<div style="margin-top:16px;border-top:1px solid #e0e0e0;padding-top:12px">${sig.signature_body}</div>`
            );
          }
          setShowSignaturePicker(false);
        }}
        currentSignatureId={appliedSignatureId}
      />
    </div>
  );
};

export default EmailTemplatesController;
